import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatDistance } from 'date-fns'
import { api } from '@/api/client'
import type { Receipt } from '@/api/client'
import {
  classificationErrorLabel,
  formatStamp,
  plural,
  reclassifySentence,
  unclassifiedLines,
  waitedLong,
} from '@/lib/receipt-classification'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { formatDate } from './supplies-dashboard'
import { useHouses } from './supplies-unmatched'

/**
 * Receipt classification (CAP-683). Since CAP-675 admin-api classifies a
 * receipt's lines in the background: rules and earlier answers fill in at
 * once, the rest waits on a queue for the model, which can take as long as
 * the model is offline. Each receipt says where it stands
 * (`classification_status`); this page only reads that, and its one action
 * is POST /admin/receipts/reclassify, which asks again for every receipt with
 * an unclassified line, failed ones included.
 */

/**
 * Where one receipt's classification stands, for the receipt dialog. Nothing
 * for a receipt from before background classification, which has no status.
 */
export function ClassificationNote({ receipt: r }: { receipt: Receipt }) {
  if (r.classification_status === 'pending') {
    const since = r.classification_requested_at
    return (
      <p className="mb-3 rounded-sm border border-amber/30 bg-warningBg px-3 py-2 text-xs text-amber">
        Waiting for the model
        {since ? ` since ${formatStamp(since)}` : ''}. Lines it has not answered yet show as Not
        classified yet.
      </p>
    )
  }
  if (r.classification_status === 'failed') {
    return (
      <p
        className="mb-3 rounded-sm border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger"
        title={r.classification_error ?? undefined}
      >
        Classification failed: {classificationErrorLabel(r.classification_error)}. Re-run it from
        Receipt classification on the Supplies page.
      </p>
    )
  }
  if (r.classification_status === 'done') {
    const unresolved = r.classification_unresolved ?? 0
    return (
      <p className="mb-3 text-xs text-textFaint">
        Classified
        {r.classification_completed_at ? ` ${formatStamp(r.classification_completed_at)}` : ''}.
        {unresolved > 0
          ? ` The model gave no answer for ${plural(unresolved, 'line')}, so ${unresolved === 1 ? 'it stays' : 'they stay'} unclassified.`
          : ''}
      </p>
    )
  }
  return null
}

/**
 * The receipts still waiting for the model and the ones that failed, newest
 * first; every receipt not listed is classified. Clicking a row opens it.
 */
export function UnsettledReceipts({ onOpen }: { onOpen: (r: Receipt, house: string) => void }) {
  // Both lists in one query, so they always refresh together, each settling on
  // its own so one failing to load never hides the other. Pending is read
  // first: the model only ever moves a receipt from pending to done or failed,
  // so one that settles between the two reads still shows up as failed rather
  // than in neither list. The pair is polled while anything waits (the model
  // can settle it at any moment) and while either read failed (that is the
  // retry, since a settled read never throws).
  const lists = useQuery({
    queryKey: ['receipts', 'classification'],
    queryFn: async () => {
      const pending = await settle(api.getReceiptsByClassification('pending'))
      const failed = await settle(api.getReceiptsByClassification('failed'))
      return { failed, pending }
    },
    staleTime: 60_000,
    refetchInterval: query => {
      const data = query.state.data
      if (!data) return false
      const unsettled =
        data.pending.status === 'rejected' ||
        data.failed.status === 'rejected' ||
        data.pending.value.receipts.length > 0
      return unsettled ? WAITING_POLL_MS : false
    },
  })
  const houses = useHouses()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const rerun = useMutation({
    mutationFn: () => api.reclassifyReceipts(),
    onSuccess: res => {
      toast({ title: 'Classification requested', description: reclassifySentence(res) })
      void queryClient.invalidateQueries({ queryKey: ['receipts'] })
      void queryClient.invalidateQueries({ queryKey: ['receipt'] })
      void queryClient.invalidateQueries({ queryKey: ['supplies'] })
    },
    onError: (error: Error) =>
      toast({
        title: 'Could not re-run classification',
        description: error.message,
        variant: 'destructive',
      }),
  })

  if (lists.isPending) {
    return <p className="text-sm text-textFaint">Checking receipt classification…</p>
  }
  if (lists.isError) {
    return <p className="text-sm text-danger">Could not load receipt classification.</p>
  }
  const failedPage = lists.data.failed.status === 'fulfilled' ? lists.data.failed.value : null
  const pendingPage = lists.data.pending.status === 'fulfilled' ? lists.data.pending.value : null
  if (!failedPage && !pendingPage) {
    return <p className="text-sm text-danger">Could not load receipt classification.</p>
  }
  const failed = failedPage?.receipts ?? []
  // A receipt that failed between the two reads is in both; it belongs under Failed.
  const failedIds = new Set(failed.map(r => r.id))
  const pending = (pendingPage?.receipts ?? []).filter(r => !failedIds.has(r.id))
  if (failedPage && pendingPage && failed.length === 0 && pending.length === 0) {
    // Not "every receipt is classified": a done receipt can keep lines the
    // model left unanswered, and receipts from before background
    // classification have no state at all.
    return (
      <p className="text-sm text-textMuted">
        No receipt is waiting for the model, and none failed.
      </p>
    )
  }
  // "How long" is measured to when this list was fetched, which is what it describes.
  const now = lists.dataUpdatedAt
  const houseName = (id: string | null) =>
    id ? (houses.find(h => h.id === id)?.name ?? id) : 'No house'

  // One heading per failure reason, so 20 receipts failed the same way read as one problem.
  const byReason = new Map<string, Receipt[]>()
  for (const r of failed) {
    const label = classificationErrorLabel(r.classification_error)
    byReason.set(label, [...(byReason.get(label) ?? []), r])
  }

  const notLoaded = (page: unknown, what: string) =>
    page ? null : <p className="text-sm text-danger">Could not load {what}.</p>

  return (
    <div className="space-y-4">
      {notLoaded(failedPage, 'failed receipts')}
      {failed.length > 0 && (
        <section aria-label="Failed">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-danger">
              Failed · {failed.length}
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-textFaint">
                Asks again for every receipt with an unclassified line.
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={rerun.isPending}
                onClick={() => rerun.mutate()}
              >
                {rerun.isPending ? 'Re-running…' : 'Re-run classification'}
              </Button>
            </div>
          </div>
          {failedPage?.next_cursor && <MoreNote shown={failed.length} />}
          {[...byReason.entries()].map(([reason, list]) => (
            <div key={reason} className="mt-2">
              <p className="text-xs text-danger" title={list[0].classification_error ?? undefined}>
                {reason}
                {byReason.size > 1 ? ` · ${list.length}` : ''}
              </p>
              <ReceiptRows
                receipts={list}
                what={`failed ${byReason.size > 1 ? 'this way' : ''}`.trim()}
                houseName={houseName}
                onOpen={onOpen}
                detail={r =>
                  r.classification_completed_at ? (
                    <span className="text-textFaint">
                      Failed {formatStamp(r.classification_completed_at)}
                    </span>
                  ) : null
                }
              />
            </div>
          ))}
        </section>
      )}
      {notLoaded(pendingPage, 'receipts waiting for the model')}
      {pending.length > 0 && (
        <section aria-label="Waiting for the model">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-textMuted">
            Waiting for the model · {pending.length}
          </h3>
          {pendingPage?.next_cursor && <MoreNote shown={pending.length} />}
          <ReceiptRows
            receipts={pending}
            what="waiting"
            houseName={houseName}
            onOpen={onOpen}
            detail={r => {
              const since = r.classification_requested_at
              if (!since) return <span className="text-textMuted">Waiting</span>
              const long = waitedLong(since, now)
              return (
                <span className={long ? 'text-amber' : 'text-textMuted'}>
                  Waiting since {formatStamp(since)} (
                  {formatDistance(new Date(since), now, { addSuffix: true })})
                </span>
              )
            }}
          />
        </section>
      )}
    </div>
  )
}

/** A promise's outcome as a value, like one entry of Promise.allSettled. */
async function settle<T>(promise: Promise<T>): Promise<PromiseSettledResult<T>> {
  try {
    return { status: 'fulfilled', value: await promise }
  } catch (reason) {
    return { status: 'rejected', reason }
  }
}

/** How often the lists refresh while a receipt waits for the model, or a read failed. */
const WAITING_POLL_MS = 60_000

/** The list is one page of 200; past that, say so instead of paging. */
function MoreNote({ shown }: { shown: number }) {
  return (
    <p className="mt-1 text-xs text-textFaint">
      Showing the newest {shown}; there are older ones too.
    </p>
  )
}

/** Rows shown before "Show all": enough to see the pattern without burying the page. */
const COLLAPSED_ROWS = 5

function ReceiptRows({
  receipts,
  what,
  houseName,
  onOpen,
  detail,
}: {
  receipts: Receipt[]
  /** "failed" or "waiting", for the Show all button */
  what: string
  houseName: (id: string | null) => string
  onOpen: (r: Receipt, house: string) => void
  detail: (r: Receipt) => React.ReactNode
}) {
  const [expanded, setExpanded] = useState(false)
  const shown = expanded ? receipts : receipts.slice(0, COLLAPSED_ROWS)
  return (
    <>
      <ul className="mt-1 divide-y divide-border">
        {shown.map(r => {
          const shop = r.retailer ?? r.source
          const when = formatDate(r.ordered_at.slice(0, 10))
          const house = houseName(r.property_id)
          const lines = unclassifiedLines(r)
          return (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => onOpen(r, house)}
                aria-label={`Open the receipt from ${shop}, ${when}`}
                className="-mx-2 grid w-[calc(100%+1rem)] grid-cols-1 gap-x-4 gap-y-0.5 rounded-sm px-2 py-2 text-left text-xs hover:bg-surface2 focus:outline-hidden focus:ring-2 focus:ring-mint lg:grid-cols-[minmax(0,1fr)_auto] lg:items-baseline"
              >
                <span className="min-w-0">
                  <span className="text-sm font-medium text-text">
                    {shop} · {when} · ${r.total.toFixed(2)}
                  </span>
                  <span className="block text-textMuted">
                    {house}
                    {lines > 0 ? ` · ${plural(lines, 'line')} not classified` : ''}
                  </span>
                </span>
                <span className="lg:text-right">{detail(r)}</span>
              </button>
            </li>
          )
        })}
      </ul>
      {receipts.length > COLLAPSED_ROWS && (
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          className="mt-1 rounded-sm text-xs text-mint underline-offset-2 hover:underline focus:outline-hidden focus:ring-2 focus:ring-mint"
        >
          {expanded ? 'Show fewer' : `Show all ${receipts.length} ${what}`}
        </button>
      )}
    </>
  )
}
